import React, { useState, useEffect, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { Button, TextField } from "@mui/material";
import {
  QrCodeIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import ModernProductSelector from "../../utils/Selector/ModernProductSelector";
import { fetchProducts } from "../../../api/product";
import toast from "react-hot-toast";
import QRCodeScanner from "../../utils/QRCodeScanner";

/**
 * Add Product Dialog specifically for Inventory Corrections
 * Simplified with separate dialogs for each action
 */
export const CorrectionAddProductDialog = ({
  open,
  onClose,
  onProductSelected,
  storages = [],
}) => {
  const { t } = useTranslation();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [note, setNote] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  
  // Separate dialog states
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);

  // Reset state when main dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedProducts([]);
      setNote("");
      setShowProductSelector(false);
      setShowQrScanner(false);
    }
  }, [open]);

  const handleQrCodeScan = async (scannedCode) => {
    if (!scannedCode || scannedCode.trim() === "") {
      toast.error("Skriv inn eller skann en QR-kode");
      return;
    }

    if (isSearching) return;
    setIsSearching(true);

    try {
      let response = null;
      
      if (/^\d+$/.test(scannedCode.trim())) {
        try {
          response = await fetchProducts({
            ids: scannedCode.trim(),
            page: 1,
            per_page: 1,
            fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
          });
        } catch (error) {
          console.log("Product ID search failed, trying query search");
        }
      }
      
      if (!response || !response.data || response.data.length === 0) {
        response = await fetchProducts({
          query: scannedCode.trim(),
          page: 1,
          per_page: 10,
          fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
        });
      }
      
      if (response && response.data && response.data.length > 0) {
        const product = response.data[0];
        // Add to selected products (avoid duplicates)
        setSelectedProducts((prev) => {
          if (prev.find((p) => p.id === product.id)) {
            toast.info("Produktet er allerede valgt");
            return prev;
          }
          return [...prev, product];
        });
        // Close QR scanner and return to main dialog
        setShowQrScanner(false);
        toast.success("Produkt lagt til");
      } else {
        toast.error("Produkt ikke funnet. Prøv å søke manuelt.");
      }
    } catch (error) {
      console.error("Error searching product:", error);
      toast.error("Kunne ikke søke etter produkt");
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddProducts = async () => {
    if (selectedProducts.length === 0) {
      toast.error("Velg minst ett produkt");
      return;
    }

    try {
      // Add each selected product
      for (const product of selectedProducts) {
        await onProductSelected(product, { note });
      }
      // Reset and close
      setSelectedProducts([]);
      setNote("");
      onClose();
    } catch (error) {
      console.error("Error adding products:", error);
      // Don't close dialog on error so user can try again
    }
  };

  return (
    <>
      {/* Main Dialog */}
      <Transition.Root show={open && !showProductSelector && !showQrScanner} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    Legg til korreksjonslinje
                  </Dialog.Title>

                  {selectedProducts.length === 0 ? (
                    // No products selected - show action buttons
                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={() => setShowProductSelector(true)}
                        className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <MagnifyingGlassIcon className="h-5 w-5 mr-3 text-gray-600" />
                        <span className="text-gray-700 font-medium">
                          Velg produkt
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowQrScanner(true)}
                        className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <QrCodeIcon className="h-5 w-5 mr-3 text-gray-600" />
                        <span className="text-gray-700 font-medium">
                          Legg til produkt (QR)
                        </span>
                      </button>
                      <div className="mt-4 flex justify-end">
                        <Button variant="outlined" onClick={onClose}>
                          Lukk
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Products selected - show products and note field
                    <div className="space-y-4">
                      <div className="text-sm font-medium text-gray-700">
                        Valgte produkter ({selectedProducts.length}):
                      </div>
                      {selectedProducts.map((product) => (
                        <div key={product.id} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="font-medium text-gray-900">
                            {product.displayTitle || product.title || `Produkt ${product.id}`}
                          </div>
                          {product.item_number && (
                            <div className="text-sm text-gray-500 mt-1">
                              Art.nr: {product.item_number}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedProducts(selectedProducts.filter((p) => p.id !== product.id));
                            }}
                            className="mt-2 text-sm text-red-600 hover:text-red-800"
                          >
                            Fjern
                          </button>
                        </div>
                      ))}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Notat (valgfritt)
                        </label>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          variant="outlined"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder="Legg til notat..."
                        />
                      </div>

                      <div className="flex justify-end space-x-2 mt-4">
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setSelectedProducts([]);
                            setNote("");
                          }}
                        >
                          Nullstill
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleAddProducts}
                          disabled={isSearching}
                        >
                          Legg til produkter ({selectedProducts.length})
                        </Button>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Product Selector Dialog - Separate, simpler dialog */}
      <Transition.Root show={showProductSelector} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowProductSelector(false)}>
          <Transition.Child
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Velg produkter
                    </Dialog.Title>
                    <button
                      onClick={() => setShowProductSelector(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <ModernProductSelector
                    open={true}
                    selectedProducts={selectedProducts}
                    onChange={(products) => {
                      setSelectedProducts(products);
                    }}
                    onClose={() => setShowProductSelector(false)}
                    multiple={true}
                    filters={[]}
                    showHeader={false}
                  />
                  {selectedProducts.length > 0 && (
                    <div className="mt-4 flex justify-end">
                      <Button
                        variant="contained"
                        onClick={() => {
                          setShowProductSelector(false);
                          toast.success(`${selectedProducts.length} ${selectedProducts.length === 1 ? "produkt" : "produkter"} valgt`);
                        }}
                      >
                        Velg produkter ({selectedProducts.length})
                      </Button>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* QR Scanner Dialog - Separate, simpler dialog */}
      <Transition.Root show={showQrScanner} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowQrScanner(false)}>
          <Transition.Child
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Skann QR-kode
                    </Dialog.Title>
                    <button
                      onClick={() => setShowQrScanner(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <QRCodeScanner
                    open={true}
                    onScan={handleQrCodeScan}
                    onBack={() => setShowQrScanner(false)}
                    placeholder="Skriv produktnummer eller skann QR-kode"
                    isSearching={isSearching}
                  />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};
